
import React, { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import LearningJourney from './components/LearningJourney';
import { Authentication } from './components/Authentication';
import { Dashboard } from './components/Dashboard';
import { AdminDashboard } from './components/AdminDashboard';
import ReadingTest from './components/ReadingTest';

import { LandingPage } from './components/LandingPage';
import { AboutPage } from './components/AboutPage';

import { UnifiedGameInterface } from './components/UnifiedGameInterface';
import { SpeakingTestInterface } from './components/SpeakingTestInterface';
import { LogicalTestInterface } from './components/LogicalTestInterface';
import TestResults from './components/TestResults';
import PaymentPage from './components/PaymentPage';
import PaymentResult from './components/PaymentResult';
import PaymentHistory from './components/PaymentHistory';
import { gradeLevels, Word } from './data/words';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { initializeApp } from "firebase/app";
import { submitGrade, submitWords, checkIsAdmin, getAdminBypassPayment, setAdminBypassPayment } from './services/api';
import { trackPageView } from './services/metaPixel';


// =============================================
// TYPES
// =============================================
interface User {
  id: string;
  email: string;
  name: string;
  createdAt: string;
}

interface PlayerProfile {
  id: string;
  name: string;
  age: number;
  gradeLevel: number;
  wordsCompleted: number;
  totalWordsAttempted: number;
  averageAccuracy: number;
  lastPlayed: string;
  paymentStatus?: 'paid' | 'unpaid';
}

type AppView = 'dashboard' | 'game' | 'admin' | 'learningJourney' | 'results';
type TestType = 'spelling' | 'speaking' | 'reading' | 'logic';

// =============================================
// FIREBASE CONFIG
// =============================================
const firebaseConfig = {
  apiKey: "AIzaSyAndj6cTVS48-koGsdYyVr_BLCEIu5G1tU",
  authDomain: "the-dear-parent-project.firebaseapp.com",
  projectId: "the-dear-parent-project",
  storageBucket: "the-dear-parent-project.firebasestorage.app",
  messagingSenderId: "235781574670",
  appId: "1:235781574670:web:f5e8fe651ac34667abec5f",
  measurementId: "G-SKHRXP4H06"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// =============================================
// AUTH CONTEXT
// =============================================
interface AuthContextType {
  currentUser: User | null;
  isAuthChecking: boolean;
  isAdmin: boolean;
  adminBypassPayment: boolean;
  setAdminBypassPayment: (enabled: boolean) => Promise<void>;
  login: (user: User) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// =============================================
// AUTH PROVIDER COMPONENT
// =============================================
const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize user from localStorage synchronously
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    try {
      const saved = localStorage.getItem('phonics-current-user');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminBypassPayment, setAdminBypassPaymentState] = useState(false);

  // Fetch the admin's bypass-payment toggle. Only meaningful for admins.
  const refreshAdminBypass = useCallback(async () => {
    try {
      const data = await getAdminBypassPayment();
      setAdminBypassPaymentState(!!data.enabled);
    } catch {
      setAdminBypassPaymentState(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!isMounted) return;
      
      if (firebaseUser) {
        const savedUser = localStorage.getItem('phonics-current-user');
        
        if (savedUser) {
          try {
            const user = JSON.parse(savedUser);
            setCurrentUser(user);
            
            // Check admin status
            try {
              const adminStatus = await checkIsAdmin();
              if (isMounted) setIsAdmin(adminStatus);
              if (adminStatus) {
                refreshAdminBypass();
              } else if (isMounted) {
                setAdminBypassPaymentState(false);
              }
            } catch (error) {
              console.error('Error checking admin status:', error);
              if (isMounted) {
                setIsAdmin(false);
                setAdminBypassPaymentState(false);
              }
            }
          } catch (error) {
            console.error('Error parsing saved user:', error);
            localStorage.removeItem('phonics-current-user');
            if (isMounted) setCurrentUser(null);
          }
        } else {
          if (isMounted) setCurrentUser(null);
        }
      } else {
        localStorage.removeItem('phonics-current-user');
        if (isMounted) {
          setCurrentUser(null);
          setIsAdmin(false);
          setAdminBypassPaymentState(false);
        }
      }
      
      if (isMounted) setIsAuthChecking(false);
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [refreshAdminBypass]);

  const login = useCallback(async (user: User) => {
    localStorage.setItem('phonics-current-user', JSON.stringify(user));
    setCurrentUser(user);
    
    try {
      const adminStatus = await checkIsAdmin();
      setIsAdmin(adminStatus);
      if (adminStatus) {
        refreshAdminBypass();
      } else {
        setAdminBypassPaymentState(false);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
      setAdminBypassPaymentState(false);
    }
  }, [refreshAdminBypass]);

  const logout = useCallback(async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
    localStorage.removeItem('phonics-current-user');
    localStorage.removeItem('phonics-profiles');
    setCurrentUser(null);
    setIsAdmin(false);
    setAdminBypassPaymentState(false);
  }, []);

  const updateAdminBypassPayment = useCallback(async (enabled: boolean) => {
    await setAdminBypassPayment(enabled);
    setAdminBypassPaymentState(enabled);
  }, []);

  return (
    <AuthContext.Provider value={{ currentUser, isAuthChecking, isAdmin, adminBypassPayment, setAdminBypassPayment: updateAdminBypassPayment, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// =============================================
// LOADING SPINNER COMPONENT
// =============================================
const LoadingSpinner: React.FC<{ message?: string }> = ({ message = 'Loading...' }) => (
  <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F3EEE8' }}>
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#F7B26A] border-t-transparent"></div>
      <p className="mt-4 text-lg text-[#1F224B]">{message}</p>
    </div>
  </div>
);

// =============================================
// PROTECTED ROUTE COMPONENT
// =============================================
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, isAuthChecking } = useAuth();
  const location = useLocation();

  if (isAuthChecking) {
    return <LoadingSpinner message="Checking session..." />;
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// =============================================
// PUBLIC ROUTE COMPONENT (redirects authenticated users)
// =============================================
const PublicRoute: React.FC<{ children: React.ReactNode; redirectTo?: string }> = ({ 
  children, 
  redirectTo = '/app' 
}) => {
  const { currentUser, isAuthChecking } = useAuth();

  if (isAuthChecking) {
    return <LoadingSpinner message="Loading..." />;
  }

  if (currentUser) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

// =============================================
// LOGIN PAGE COMPONENT
// =============================================
const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const from = (location.state as any)?.from?.pathname || '/app';

  const handleAuthSuccess = async (user: User) => {
    await login(user);
    navigate(from, { replace: true });
  };

  return <Authentication onAuthSuccess={handleAuthSuccess} />;
};

// =============================================
// MAIN APP CONTENT COMPONENT
// =============================================
const MainAppContent: React.FC = () => {
  const { currentUser, isAdmin, adminBypassPayment, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // App state
  const [currentView, setCurrentView] = useState<AppView>('dashboard');
  const [selectedProfile, setSelectedProfile] = useState<PlayerProfile | null>(null);
  const [selectedGradeLevel, setSelectedGradeLevel] = useState(0);
  const [wordsList, setWordsList] = useState<Word[]>([]);
  const [testType, setTestType] = useState<TestType>('spelling');
  const [isLoading, setIsLoading] = useState(false);
  const [assessmentData, setAssessmentData] = useState<any>(null);

  const currentGrade = gradeLevels[selectedGradeLevel];
  const useDragDrop = selectedGradeLevel === 0;

  // Check for incoming test start from LearningJourney navigation
  useEffect(() => {
    const state = location.state as { 
      startTest?: boolean; 
      profile?: PlayerProfile; 
      testType?: TestType;
      showLearningJourney?: boolean;
    } | null;
    
    if (state?.showLearningJourney) {
      // Clear the navigation state to prevent re-triggering
      navigate(location.pathname, { replace: true, state: {} });
      // Show learning journey view
      if (state.profile) {
        setSelectedProfile(state.profile);
        setSelectedGradeLevel(state.profile.gradeLevel);
      }
      if (selectedProfile || state.profile) {
        setCurrentView('learningJourney');
      } else {
        setCurrentView('dashboard');
      }
    } else if (state?.startTest && state?.profile && state?.testType) {
      // Clear the navigation state to prevent re-triggering
      navigate(location.pathname, { replace: true, state: {} });
      // Start the test
      handleStartGame(state.profile, state.profile.gradeLevel, state.testType);
    }
  }, [location.state]);

  // Load saved view preference on mount
  useEffect(() => {
    const savedView = localStorage.getItem('admin-view-preference');
    if (savedView === 'user' && isAdmin) {
      setCurrentView('dashboard');
    } else if (isAdmin && currentView === 'dashboard') {
      // Only auto-redirect to admin on initial login if no preference saved
      setCurrentView('admin');
    }
  }, [isAdmin]);

  const handleStartGame = async (
    profile: PlayerProfile, 
    gradeLevel: number, 
    selectedTestType: TestType = 'spelling'
  ) => {
    // Payment gate: unpaid children cannot take tests.
    // The backend also enforces this (402) on every test endpoint.
    // Admins with the bypass-payment toggle on skip this gate.
    const bypassActive = isAdmin && adminBypassPayment;
    if (!bypassActive && profile.paymentStatus !== 'paid') {
      navigate('/app/payment', { state: { preselectChildId: profile.id } });
      return;
    }
    try {
      setIsLoading(true);
      setSelectedProfile(profile);
      setSelectedGradeLevel(gradeLevel);
      setTestType(selectedTestType);
      
      const gradeName = gradeLevels[gradeLevel].name.replace(/ grade$/i, '').trim();
      
      if (selectedTestType === 'spelling') {
        const wordResponse = await submitGrade(gradeName);
        setWordsList(wordResponse);
      }
      // Reading and Logic tests use API to fetch data, no need to set wordsList
      
      setCurrentView('game');
    } catch (error) {
      console.error('Error starting game:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWordComplete = async (results: any) => {
    try {
      setIsLoading(true);
      const gradeName = gradeLevels[selectedGradeLevel].name.replace(/ grade$/i, '').trim();

      // Spelling is the only test that still needs its answers posted from here;
      // the other test interfaces submit as they go.
      if (testType === 'spelling' && (Array.isArray(results) || (results && 'words' in results))) {
        const wordsArray = Array.isArray(results) ? results : results.words;
        await submitWords(wordsArray, selectedProfile?.id, gradeName);
      }

      // No per-test result screen: the child goes straight back to the journey
      // and only the combined final result is shown at the end.
      setAssessmentData(null);
      setCurrentView('learningJourney');
    } catch (error) {
      console.error('Error submitting test:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
    setSelectedProfile(null);
    setSelectedGradeLevel(0);
    setAssessmentData(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  // Loading state
  if (isLoading) {
    return <LoadingSpinner message="Loading..." />;
  }

  // Admin view
  if (currentView === 'admin' && isAdmin) {
    return (
      <div className="app-page">
        <div className="app-container">
          <AdminDashboard 
            currentUser={currentUser!}
            onLogout={handleLogout}
            onSwitchToDashboard={() => {
              localStorage.setItem('admin-view-preference', 'user');
              setCurrentView('dashboard');
            }}
          />
        </div>
      </div>
    );
  }

  // Game view
  if (currentView === 'game' && selectedProfile) {
    if (testType === 'logic') {
      return (
        <div className="app-page">
          <div className="app-container">
            <LogicalTestInterface
              onComplete={({ responses, summary }) => handleWordComplete({ responses, summary })}
              onBackToDashboard={() => setCurrentView('learningJourney')}
              playerName={selectedProfile.name}
              gradeName={currentGrade.name}
              childId={selectedProfile.id}
            />
          </div>
        </div>
      );
    }

    if (testType === 'speaking') {
      return (
        <div className="app-page">
          <div className="app-container">
            <SpeakingTestInterface
              words={wordsList}
              onComplete={({ words }) => handleWordComplete(words)}
              onBackToDashboard={() => setCurrentView('learningJourney')}
              playerName={selectedProfile.name}
              gradeName={currentGrade.name}
              childId={selectedProfile.id}
            />
          </div>
        </div>
      );
    }

    if (testType === 'reading') {
      return (
        <div className="app-page">
          <div className="app-container">
            <ReadingTest
              onComplete={(results) => {
                console.log('Reading test completed:', results);
                handleWordComplete(results);
              }}
              onBackToDashboard={() => setCurrentView('learningJourney')}
              playerName={selectedProfile.name}
              gradeName={currentGrade.name}
              childId={selectedProfile.id}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="app-page">
        <div className="app-container">
          <UnifiedGameInterface
            words={wordsList}
            onComplete={({ words }) => handleWordComplete(words)}
            onBackToDashboard={() => setCurrentView('learningJourney')}
            isDragMode={useDragDrop}
            playerName={selectedProfile.name}
            gradeName={currentGrade.name}
          />
        </div>
      </div>
    );
  }

  // LearningJourney view
  if (currentView === 'learningJourney' && selectedProfile) {
    return (
      <div className="app-page">
        <div className="app-container">
          <LearningJourney
            childName={selectedProfile.name}
            childId={selectedProfile.id}
            gradeLevel={selectedProfile.gradeLevel}
            isPaid={selectedProfile.paymentStatus === 'paid' || (isAdmin && adminBypassPayment)}
            isAdmin={isAdmin}
            onStartTest={(testType: 'spelling' | 'speaking' | 'reading' | 'logic') => {
              handleStartGame(selectedProfile, selectedProfile.gradeLevel, testType);
            }}
            onViewFinal={() => {
              const gradeInfo = gradeLevels[selectedProfile.gradeLevel];
              setAssessmentData({
                user_name: selectedProfile.name,
                user_age: selectedProfile.age,
                grade: gradeInfo?.name || 'Unknown',
                testType: 'final'
              });
              setCurrentView('results');
            }}
            onBack={() => {
              setCurrentView('dashboard');
              setSelectedProfile(null);
            }}
          />
        </div>
      </div>
    );
  }

  // Results view (the combined final result only)
  if (currentView === 'results' && assessmentData) {
    // The snapshot letter is a full-bleed page of its own, so it skips the app chrome.
    return (
      <TestResults
        assessmentData={assessmentData}
        childId={selectedProfile?.id || ''}
        grade={assessmentData?.grade}
        onBackToDashboard={() => {
          setCurrentView('learningJourney');
          setAssessmentData(null);
        }}
      />
    );
  }

  // Default: Dashboard view
  return (
    <div className="app-page">
      <div className="app-container">
        <Dashboard 
          onStartGame={handleStartGame} 
          currentUser={currentUser!}
          onLogout={handleLogout}
          isAdmin={isAdmin}
          adminBypassPayment={adminBypassPayment}
          onSwitchToAdmin={() => {
            localStorage.setItem('admin-view-preference', 'admin');
            setCurrentView('admin');
          }}
        />
      </div>
    </div>
  );
};

// =============================================
// META PIXEL
// =============================================
// The browser loads this SPA exactly once, so the pixel would otherwise record
// a single PageView for a whole session no matter how far the visitor got.
// Firing on every location change gives Meta the funnel it needs to optimise
// delivery and to attribute conversions back to an ad.
//
// trackPageView ignores any route that is not parent-facing, so the Learning
// Snapshot preview — and anything child-facing added later — reports nothing.
const MetaPixelRouteTracker: React.FC = () => {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    const path = location.pathname + location.search;
    // StrictMode runs effects twice in development; without this the first
    // route of every dev session would be counted twice.
    if (lastPath.current === path) return;
    lastPath.current = path;
    trackPageView(location.pathname);
  }, [location]);

  return null;
};

// =============================================
// APP ROUTES
// =============================================
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Landing page - redirect to /app if logged in */}
      <Route 
        path="/" 
        element={
          <PublicRoute>
            <LandingPage />
          </PublicRoute>
        } 
      />
      
      {/* About page - public */}
      <Route 
        path="/about" 
        element={
          <PublicRoute>
            <AboutPage />
          </PublicRoute>
        } 
      />
      
      {/* Login page - redirect to /app if logged in */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      
      {/* Main app - requires authentication */}
      <Route 
        path="/app" 
        element={
          <ProtectedRoute>
            <MainAppContent />
          </ProtectedRoute>
        } 
      />
      
      {/* Payment page - unlock child assessments */}
      <Route 
        path="/app/payment" 
        element={
          <ProtectedRoute>
            <PaymentPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Payment history - view past payments and status */}
      <Route 
        path="/app/payment-history" 
        element={
          <ProtectedRoute>
            <PaymentHistory />
          </ProtectedRoute>
        } 
      />
      
      {/* Stripe Checkout return URLs */}
      <Route 
        path="/payment/success" 
        element={
          <ProtectedRoute>
            <PaymentResult />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/payment/cancelled" 
        element={
          <ProtectedRoute>
            <PaymentResult cancelled />
          </ProtectedRoute>
        } 
      />
      
      {/* Snapshot preview - development only: the real result page, which falls
          back to the reference letter when the API has nothing for this child */}
      {process.env.NODE_ENV !== 'production' && (
        <Route
          path="/snapshot-preview"
          element={<TestResults />}
        />
      )}
      
      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// =============================================
// MAIN APP COMPONENT
// =============================================
export default function App() {
  return (
    <Router>
      <AuthProvider>
        <MetaPixelRouteTracker />
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}
